import { useState, useMemo, useCallback } from 'react';
import {
  normalizeIcons,
  computeSlots,
  createNewIcon,
  applyIconEdit,
  removeIcon,
} from '../../services/homeLauncher/launcherLayoutService';

/**
 * Manages the icon array and all mutation operations.
 *
 * @param {object} opts
 * @param {number}   opts.gridMax            – columns × rows
 * @param {string}   opts.uid                – current user uid (for save key)
 * @param {Function} opts.saveLayout         – from useLauncherPersistence
 * @param {Function} opts.getResponsiveConfig – from useLauncherResponsive.getConfig
 */
export default function useLauncherLayout({ gridMax, uid, saveLayout, getResponsiveConfig }) {
  const [icons, setIcons] = useState([]);

  const orderedIcons = useMemo(
    () => [...icons].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [icons],
  );

  const slots = useMemo(() => computeSlots(icons, gridMax), [icons, gridMax]);

  /** Persist icons with the current responsive config snapshot. */
  const _persist = useCallback(
    (nextIcons) => {
      if (!uid) return;
      saveLayout(uid, nextIcons, getResponsiveConfig());
    },
    [uid, saveLayout, getResponsiveConfig],
  );

  /** Initialise icons from raw loaded data (icons array, possibly empty). */
  const applyLayoutData = useCallback((rawIcons) => {
    setIcons(normalizeIcons(rawIcons));
  }, []);

  /** Add a new blank icon. Returns the new icon object (caller may open edit dialog). */
  const handleAddIcon = useCallback(() => {
    if (icons.length >= gridMax) return null;
    const newIcon = createNewIcon(icons, gridMax);
    const updated = [...icons, newIcon];
    setIcons(updated);
    _persist(updated.map((it) => ({ ...it, position: it.position ?? 0 })));
    return newIcon;
  }, [icons, gridMax, _persist]);

  /** Save edits to an existing icon identified by iconId. */
  const handleSaveIcon = useCallback(
    (iconId, changes) => {
      const updated = applyIconEdit(icons, iconId, changes);
      setIcons(updated);
      _persist(updated);
    },
    [icons, _persist],
  );

  /** Delete an icon by id (called from grid's delete badge). */
  const handleDeleteById = useCallback(
    (iconId) => {
      if (!iconId || iconId === 'settings') return;
      const filtered = removeIcon(icons, iconId);
      setIcons(filtered);
      _persist(filtered.map((it) => ({ ...it, position: it.position ?? 0 })));
    },
    [icons, _persist],
  );

  /**
   * Persist a reordered icons array produced by the DnD hook.
   * setIcons is called by the DnD hook directly for immediate visual update;
   * this method only handles the persistence side.
   */
  const handleReorder = useCallback(
    (reorderedIcons) => {
      console.log(
        '[HomeLauncher] handleReorder called with:',
        reorderedIcons.map((i) => `${i.id}@${i.position}`),
      );
      _persist(reorderedIcons.map((it) => ({ ...it, position: it.position ?? 0 })));
    },
    [_persist],
  );

  return {
    icons,
    setIcons,
    orderedIcons,
    slots,
    applyLayoutData,
    handleAddIcon,
    handleSaveIcon,
    handleDeleteById,
    handleReorder,
  };
}
